/**
 * Inventory Admin — Phase 2.
 *
 * Isolated admin surface: manages IN STOCK / OUT OF STOCK per sticker
 * per parallel category. Every toggle saves immediately (no Save
 * button). Reads sticker definitions from the master DB via the
 * backend, so there is zero duplication of sticker metadata.
 *
 * NOTE (per sprint brief):
 * - Not integrated with Quotes.
 * - Not integrated with Fulfillment.
 * - Not surfaced in customer flows.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckSquare, Square, Loader2, Search, RefreshCw, AlertTriangle, Copy } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { buildSoldOutPayload } from "@/lib/soldOutList";

const CATEGORIES = [
  { value: "white_regular", label: "White Regular" },
  { value: "blue_parallel", label: "Blue Parallel" },
  { value: "gold_crumple",  label: "Gold Crumple" },
  { value: "blue_crumple",  label: "Blue Crumple" },
  { value: "coca_cola",     label: "Coca-Cola" },
];

export default function InventoryAdmin() {
  const [category, setCategory] = useState(CATEGORIES[0].value);
  const [q, setQ] = useState("");

  return (
    <div className="space-y-5" data-testid="inventory-admin">
      <header>
        <div className="kicker kicker-red">Admin · Inventory</div>
        <h1 className="text-3xl font-bold mt-1">Inventory Management</h1>
        <p className="text-[var(--smoke)] text-sm mt-1 max-w-2xl">
          Track stock per sticker parallel category. Changes save immediately —
          uncheck any sticker that is out of stock. Categories are independent.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-2" data-testid="inventory-category-tabs">
        {CATEGORIES.map((c) => (
          <button
            key={c.value}
            type="button"
            data-testid={`inventory-tab-${c.value}`}
            onClick={() => setCategory(c.value)}
            aria-pressed={category === c.value}
            className={
              "px-4 py-2 rounded-lg border-2 text-sm font-bold transition " +
              (category === c.value
                ? "border-[var(--panini-red)] bg-red-50 text-[var(--panini-red)]"
                : "border-[var(--ink)] bg-white text-[var(--ink)] hover:bg-gray-50")
            }
          >
            {c.label}
          </button>
        ))}
      </div>

      <InventoryCategoryView
        category={category}
        query={q}
        setQuery={setQ}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
function InventoryCategoryView({ category, query, setQuery }) {
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  // Per-code in-flight busy flags so the row shows a spinner without
  // blocking other toggles.
  const [busy, setBusy] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const reqIdRef = useRef(0);

  const load = useCallback(async () => {
    const rid = ++reqIdRef.current;
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const r = await api.get(`/admin/inventory/${category}`);
      if (rid !== reqIdRef.current) return;
      setState({ loading: false, data: r.data, error: "" });
    } catch (e) {
      if (rid !== reqIdRef.current) return;
      setState({ loading: false, data: null, error: formatApiError(e?.response?.data?.detail) || e.message || "Load failed." });
    }
  }, [category]);

  useEffect(() => { load(); }, [load]);

  const stickers = state.data?.stickers || [];
  const counts = state.data?.counts || { total: 0, in_stock: 0, out_of_stock: 0 };
  const initialized = state.data?.initialized ?? false;
  const updatedAt = state.data?.updated_at;
  const updatedBy = state.data?.updated_by;
  const categoryLabel = state.data?.label || category;

  // Bulk confirmation dialog state.
  const [bulkConfirm, setBulkConfirm] = useState(null); // { inStock: bool }

  // Phase 2 · Sort control. Default album order (server-side canonical).
  // Client-side re-sorts are pure display; no writes, no state mutation
  // on `stickers`. On category switch we preserve the last selection.
  const [sortMode, setSortMode] = useState("album"); // album | code | player

  // Natural-order key so ARG-2 sorts before ARG-10 in code-A→Z mode.
  const _naturalKey = (code) => {
    // Accept both `ALG1` and `ALG-1` — the master price list stores the
    // compact form while the parser emits the dashed form.
    const m = /^([A-Z]{2,15})-?(\d+)$/.exec(String(code || "").toUpperCase());
    return m ? [m[1], parseInt(m[2], 10)] : [String(code || "").toUpperCase(), Number.MAX_SAFE_INTEGER];
  };

  const filtered = useMemo(() => {
    const n = query.trim().toLowerCase();
    const base = !n ? stickers : stickers.filter((s) =>
      s.code.toLowerCase().includes(n)
      || (s.player_name || "").toLowerCase().includes(n)
      || (s.country || "").toLowerCase().includes(n)
    );
    if (sortMode === "album") return base; // server delivers canonical album order
    const sorted = [...base];
    if (sortMode === "code") {
      sorted.sort((a, b) => {
        const [pa, na] = _naturalKey(a.code);
        const [pb, nb] = _naturalKey(b.code);
        if (pa < pb) return -1;
        if (pa > pb) return 1;
        return Number(na) - Number(nb);
      });
    } else if (sortMode === "player") {
      // Named rows first (A→Z), unnamed rows appended in album order.
      const named = sorted.filter((r) => (r.player_name || "").trim());
      const unnamed = sorted.filter((r) => !(r.player_name || "").trim());
      named.sort((a, b) => (a.player_name || "").localeCompare(b.player_name || ""));
      return [...named, ...unnamed];
    }
    return sorted;
  }, [stickers, query, sortMode]);

  async function toggleOne(code, nextInStock) {
    setBusy((b) => ({ ...b, [code]: true }));
    // Optimistic — flip locally first so the UI is instant.
    setState((s) => ({
      ...s,
      data: s.data && {
        ...s.data,
        stickers: s.data.stickers.map((r) =>
          r.code === code ? { ...r, in_stock: nextInStock } : r),
        counts: {
          ...s.data.counts,
          in_stock: s.data.counts.in_stock + (nextInStock ? 1 : -1),
          out_of_stock: s.data.counts.out_of_stock + (nextInStock ? -1 : 1),
        },
      },
    }));
    try {
      await api.post(`/admin/inventory/${category}/toggle`, { code, in_stock: nextInStock });
    } catch (e) {
      // Roll back on failure.
      toast.error(formatApiError(e?.response?.data?.detail) || "Save failed.");
      setState((s) => ({
        ...s,
        data: s.data && {
          ...s.data,
          stickers: s.data.stickers.map((r) =>
            r.code === code ? { ...r, in_stock: !nextInStock } : r),
          counts: {
            ...s.data.counts,
            in_stock: s.data.counts.in_stock + (nextInStock ? -1 : 1),
            out_of_stock: s.data.counts.out_of_stock + (nextInStock ? 1 : -1),
          },
        },
      }));
    } finally {
      setBusy((b) => { const c = { ...b }; delete c[code]; return c; });
    }
  }

  async function bulk(inStock) {
    setBulkBusy(true);
    try {
      const r = await api.post(`/admin/inventory/${category}/bulk`, {
        codes: null, in_stock: inStock,
      });
      toast.success(
        inStock
          ? `All stickers marked IN STOCK (${r.data?.affected ?? 0} updated).`
          : `All stickers marked OUT OF STOCK (${r.data?.affected ?? 0} updated).`
      );
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Bulk update failed.");
    } finally {
      setBulkBusy(false);
    }
  }

  return (
    <div className="border-2 border-[var(--ink)] rounded-2xl bg-white shadow-sm">
      {/* Summary + Controls */}
      <div className="p-4 border-b-2 border-[var(--ink)] flex flex-wrap items-center gap-3" data-testid="inventory-summary">
        <div className="text-xs uppercase tracking-widest text-[var(--smoke)]">
          Total: <b>{counts.total}</b> · In stock: <b className="text-emerald-800">{counts.in_stock}</b> · Out of stock: <b className="text-red-800">{counts.out_of_stock}</b>
        </div>
        {initialized ? (
          <span
            className="inline-flex items-center gap-1 border-2 border-emerald-800 bg-emerald-50 text-emerald-900 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
            data-testid="inventory-initialized-badge"
            title={updatedAt ? `Last updated ${new Date(updatedAt).toLocaleString()} · ${updatedBy || ""}` : ""}
          >
            Initialized
          </span>
        ) : (
          <span
            className="inline-flex items-center gap-1 border-2 border-amber-800 bg-amber-50 text-amber-900 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest"
            data-testid="inventory-not-initialized-badge"
          >
            <AlertTriangle size={11}/> Not Initialized · use Select All to start
          </span>
        )}
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setBulkConfirm({ inStock: true })}
          disabled={bulkBusy}
          className="inline-flex items-center gap-1.5 border-2 border-[var(--ink)] rounded-lg px-3 py-1.5 text-xs font-bold uppercase tracking-widest hover:bg-emerald-50 disabled:opacity-40"
          data-testid="inventory-select-all"
        >
          {bulkBusy ? <Loader2 className="animate-spin" size={12}/> : <CheckSquare size={12}/>} Select All
        </button>
        <button
          type="button"
          onClick={() => setBulkConfirm({ inStock: false })}
          disabled={bulkBusy}
          className="inline-flex items-center gap-1.5 border-2 border-[var(--ink)] rounded-lg px-3 py-1.5 text-xs font-bold uppercase tracking-widest hover:bg-red-50 disabled:opacity-40"
          data-testid="inventory-unselect-all"
        >
          {bulkBusy ? <Loader2 className="animate-spin" size={12}/> : <Square size={12}/>} Unselect All
        </button>
        {/* Phase A · Copy Sold-Out Stickers.
            Uses the sticker list already loaded for the active category —
            no extra backend request. Filters to `!in_stock` rows and
            formats via the shared `soldOutList` helper. */}
        <button
          type="button"
          onClick={async () => {
            const soldOutCodes = stickers
              .filter((s) => !s.in_stock)
              .map((s) => s.code);
            const payload = buildSoldOutPayload(soldOutCodes);
            try {
              if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(payload.text);
              } else {
                const ta = document.createElement("textarea");
                ta.value = payload.text;
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
              }
              toast.success(
                payload.isEmpty
                  ? "Copied — all tracked stickers are in stock."
                  : `Copied ${payload.totalCodes} sold-out sticker${payload.totalCodes === 1 ? "" : "s"}.`
              );
            } catch {
              toast.error("Copy failed — please try again.");
            }
          }}
          disabled={!initialized}
          title={initialized
            ? "Copy the current sold-out list to the clipboard"
            : "Initialize this category first"}
          className="inline-flex items-center gap-1.5 border-2 border-[var(--ink)] rounded-lg px-3 py-1.5 text-xs font-bold uppercase tracking-widest hover:bg-amber-50 disabled:opacity-40"
          data-testid="inventory-copy-sold-out"
        >
          <Copy size={12}/> Copy Sold-Out
        </button>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[var(--smoke)] hover:text-[var(--ink)]"
          data-testid="inventory-refresh"
        >
          <RefreshCw size={12}/> Refresh
        </button>
      </div>
      {updatedAt && (
        <div className="px-4 py-1 text-[10px] text-[var(--smoke)] border-b border-[var(--stone)]" data-testid="inventory-last-updated">
          Last updated {new Date(updatedAt).toLocaleString()}{updatedBy ? ` · by ${updatedBy}` : ""}
        </div>
      )}

      {/* Bulk confirmation dialog */}
      {bulkConfirm && (
        <BulkConfirmDialog
          inStock={bulkConfirm.inStock}
          categoryLabel={categoryLabel}
          onCancel={() => setBulkConfirm(null)}
          onConfirm={async () => {
            const wanted = bulkConfirm.inStock;
            setBulkConfirm(null);
            await bulk(wanted);
          }}
        />
      )}

      <div className="p-4 border-b border-[var(--stone)]">
        <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <div className="relative flex-1">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by code, player, or country…"
              className="w-full border-2 border-[var(--ink)] rounded-lg px-3 py-2 pr-9"
              data-testid="inventory-search"
            />
            <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[var(--smoke)]" />
          </div>
          {/* Phase 2 · Sort control (display only — never mutates inventory). */}
          <label className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[var(--smoke)]">
            Sort
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              className="border-2 border-[var(--ink)] rounded-lg px-2 py-1.5 text-sm font-normal normal-case tracking-normal"
              data-testid="inventory-sort"
            >
              <option value="album">Album Order</option>
              <option value="code">Sticker Code A–Z</option>
              <option value="player">Player / Subject A–Z</option>
            </select>
          </label>
        </div>
        {query.trim() && (
          <div className="text-[11px] text-[var(--smoke)] mt-2" data-testid="inventory-result-count">
            {filtered.length} result{filtered.length === 1 ? "" : "s"}
          </div>
        )}
      </div>

      {/* Sticker list */}
      {state.loading ? (
        <div className="p-16 text-center"><Loader2 className="animate-spin inline" size={20}/></div>
      ) : state.error ? (
        <div className="p-8 text-center text-red-800 text-sm" data-testid="inventory-error">{state.error}</div>
      ) : (
        <div className="max-h-[70vh] overflow-y-auto" data-testid={`inventory-list-${category}`}>
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[var(--panini-yellow-soft)] z-10">
              <tr className="text-[10px] uppercase tracking-widest border-b-2 border-[var(--ink)]">
                <th className="px-4 py-2 text-left w-10">Stock</th>
                <th className="px-4 py-2 text-left">Code</th>
                <th className="px-4 py-2 text-left">Country</th>
                <th className="px-4 py-2 text-left">Player / Subject</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => {
                const isBusy = !!busy[row.code];
                return (
                  <tr
                    key={row.code}
                    className={"border-b border-[var(--stone)] hover:bg-[var(--panini-yellow-soft)]/40 " + (row.in_stock ? "" : "bg-red-50/40")}
                    data-testid={`inventory-row-${row.code}`}
                  >
                    <td className="px-4 py-2">
                      <button
                        type="button"
                        role="checkbox"
                        aria-checked={row.in_stock}
                        aria-label={`Toggle ${row.code} ${row.in_stock ? "to out of stock" : "to in stock"}`}
                        onClick={() => toggleOne(row.code, !row.in_stock)}
                        disabled={isBusy}
                        className="inline-flex items-center justify-center w-6 h-6"
                        data-testid={`inventory-toggle-${row.code}`}
                      >
                        {isBusy ? (
                          <Loader2 className="animate-spin" size={14}/>
                        ) : row.in_stock ? (
                          <CheckSquare size={18} className="text-emerald-700" />
                        ) : (
                          <Square size={18} className="text-red-700" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-2 font-mono font-bold" data-testid={`inventory-code-${row.code}`}>{row.code}</td>
                    <td className="px-4 py-2 text-[var(--smoke)]">{row.country || "—"}</td>
                    <td className="px-4 py-2">{row.player_name || <span className="text-[var(--smoke)]">—</span>}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-[var(--smoke)]" data-testid="inventory-empty">
                    No stickers match this search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Bulk-action confirmation. Explicit label per user spec:
//   "Mark every White Regular sticker as In Stock?"
//   "Mark every Blue Parallel sticker as Out of Stock?"
// Bulk actions ALWAYS apply to the entire category (never just the
// filtered search results) — the dialog wording states so.
function BulkConfirmDialog({ inStock, categoryLabel, onCancel, onConfirm }) {
  const action = inStock ? "In Stock" : "Out of Stock";
  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="inventory-bulk-confirm-title"
      data-testid="inventory-bulk-confirm"
    >
      <div className="bg-white border-2 border-[var(--ink)] rounded-2xl max-w-md w-full p-6 shadow-2xl">
        <h3 id="inventory-bulk-confirm-title" className="text-lg font-bold">Confirm bulk update</h3>
        <p className="mt-2 text-sm text-[var(--ink)]">
          Mark <b>every</b> {categoryLabel} sticker as <b>{action}</b>?
        </p>
        <p className="mt-1 text-xs text-[var(--smoke)]">
          This applies to the entire category (not just the current search results).
        </p>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            data-testid="inventory-bulk-cancel"
            className="border-2 border-[var(--ink)] rounded-lg px-4 py-2 text-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            data-testid="inventory-bulk-confirm-ok"
            className={"rounded-lg px-4 py-2 text-sm font-bold text-white " +
              (inStock ? "bg-emerald-800 hover:bg-emerald-900" : "bg-red-800 hover:bg-red-900")}
          >
            Yes, mark all {action}
          </button>
        </div>
      </div>
    </div>
  );
}
